import "@servicetitan/anvil-fonts/dist/css/anvil-fonts.css";
import "@servicetitan/design-system/dist/system.min.css";
import { BreakPoint } from "@servicetitan/design-system";
import { DesktopLandingPage } from "./components/DesktopLandingPage";
import { MobileLandingPage } from "./components/MobileLandingPage";
import { NotFoundPage } from "./components/NotFoundPage";
import { useState, useEffect } from "react";
import {
  IClientState,
  IGetRwgConfigResponseV1,
  IGetRwgConfigResponseV2,
} from "./types/App";
import { InitialPage } from "./components/InitialPage";
import { parseRoute, fetchRwgConfig } from "./utils/routeUtils";
import * as Sentry from "@sentry/react";

function App() {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.REACT_APP_ENVIRONMENT,
    integrations: [Sentry.browserTracingIntegration()],
    release: process.env.REACT_APP_VERSION,
    tracesSampleRate: 1.0,
  });

  const [clientData, setClientData] = useState<IClientState>();

  const isV2 = "schedulerSrc" in (clientData?.data ?? {});
  const { addressLine, mediaUrl, name } = clientData?.data || {
    addressLine: "",
    mediaUrl: "",
    name: "",
  };

  // V1 fields
  const clientKey = isV2
    ? ""
    : (clientData?.data as IGetRwgConfigResponseV1)?.clientKey;
  const flowKey = isV2
    ? ""
    : (clientData?.data as IGetRwgConfigResponseV1)?.flowKey;

  // V2 fields
  const schedulerSrc = isV2
    ? (clientData?.data as IGetRwgConfigResponseV2)?.schedulerSrc
    : "";
  const validOrigins = isV2
    ? (clientData?.data as IGetRwgConfigResponseV2)?.validOrigins
    : undefined;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const routeParams = parseRoute();
        const result = await fetchRwgConfig(routeParams);
        setClientData(result);
      } catch (error) {
        setClientData({
          data: undefined,
          requestSuccess: false,
        });
        Sentry.captureException(error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (name) {
      document.title = name;
    }
  }, [name]);

  if (!clientData) {
    return <InitialPage />;
  }

  if (!clientData.requestSuccess) {
    return <NotFoundPage />;
  }

  return (
    <Sentry.ErrorBoundary>
      <BreakPoint max="mobile">
        <MobileLandingPage
          addressLine={addressLine}
          mediaUrl={mediaUrl}
          name={name}
          {...(isV2 ? { schedulerSrc, validOrigins } : { clientKey, flowKey })}
        />
      </BreakPoint>
      <BreakPoint min="mobile">
        <DesktopLandingPage
          addressLine={addressLine}
          mediaUrl={mediaUrl}
          name={name}
          {...(isV2 ? { schedulerSrc, validOrigins } : { clientKey, flowKey })}
        />
      </BreakPoint>
    </Sentry.ErrorBoundary>
  );
}

export default App;
